import { useState, useEffect, useMemo } from "react"
import { useLocation, useSearch } from "wouter"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { Skeleton } from "@/components/ui/skeleton"
import { useToast } from "@/hooks/use-toast"
import { useInfiniteQuery, useQuery, useMutation, useQueryClient, type InfiniteData } from "@tanstack/react-query"
import { apiRequest } from "@/lib/queryClient"
import type { IndustryNews } from "@shared/schema"
import {
  Newspaper,
  TrendingUp,
  Filter,
  ExternalLink,
  Eye,
  Loader2,
  Trash2,
  ArrowUpDown,
} from "lucide-react"
import { FloatingCard } from "@/components/FloatingCard"
import { AnimatedSection } from "@/components/AnimatedSection"
import { NewsPostsModal } from "@/components/NewsPostsModal"
import { RelevanceScore } from "@/components/RelevanceScore"
import { ScheduledPlanLink } from "@/components/ScheduledPlanLink"
import { StatCard } from "@/components/StatCard"
import { statusConfig } from "@/lib/status-config"

// Server shape of GET /api/industry-news — article status is DERIVED from the article's
// generated posts (≥1 posted/scheduled → "approved"); dates arrive as ISO strings.
type NewsListItem = IndustryNews & {
  postsCount: number
  derivedStatus: "pending" | "approved"
  nextScheduledFor: string | null
  nextScheduledPostId: string | null
}

interface NewsListResponse {
  items: NewsListItem[]
  total: number
  stats: { total: number; pending: number; approved: number; highRelevance: number }
}

type StatusFilter = "all" | "pending" | "approved"
type SortOrder = "newest" | "oldest" | "relevance"

const PAGE_SIZE = 20

export default function IndustryNews() {
  const [selectedStatus, setSelectedStatus] = useState<StatusFilter>("all")
  const [sortOrder, setSortOrder] = useState<SortOrder>("newest")
  const [selectedNews, setSelectedNews] = useState<IndustryNews | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [deleteConfirmNews, setDeleteConfirmNews] = useState<NewsListItem | null>(null)
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [, setLocation] = useLocation()

  // Deep-link from the Posting-Plan: ?news=<articleId>&platform=<tab> opens the exact
  // post in the Post-Vorschau modal (photo-module parity).
  const search = useSearch()
  const searchParams = useMemo(() => new URLSearchParams(search), [search])
  const deepLinkNewsId = searchParams.get("news")
  const deepLinkPlatform = searchParams.get("platform") ?? undefined

  const { data: deepLinkedNews, error: deepLinkError } = useQuery<IndustryNews>({
    queryKey: ['/api/industry-news/item', deepLinkNewsId],
    queryFn: () => apiRequest('GET', `/api/industry-news/${deepLinkNewsId}`),
    enabled: !!deepLinkNewsId,
  })

  useEffect(() => {
    if (deepLinkedNews) {
      setSelectedNews(deepLinkedNews)
      setIsModalOpen(true)
    }
  }, [deepLinkedNews])

  // A dead/foreign id must not fail silently (retry is globally off): say so and clean
  // the URL, otherwise ?news= sticks forever because the modal never opens/closes.
  useEffect(() => {
    if (deepLinkError && deepLinkNewsId) {
      toast({
        title: "News nicht gefunden",
        description: "Der verlinkte Artikel existiert nicht mehr.",
        variant: "destructive",
      })
      setLocation("/industry-news", { replace: true })
    }
  }, [deepLinkError, deepLinkNewsId, toast, setLocation])

  const deleteMutation = useMutation({
    mutationFn: async (newsId: string) => {
      await apiRequest('DELETE', `/api/industry-news/${newsId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/industry-news'] })
      toast({
        title: "Gelöscht",
        description: "Die Industry News wurde gelöscht."
      })
      setDeleteConfirmNews(null)
    },
    onError: (error: Error) => {
      toast({
        title: "Fehler",
        description: error.message || "Löschen fehlgeschlagen.",
        variant: "destructive"
      })
    }
  })

  const handleViewPosts = (news: NewsListItem) => {
    setSelectedNews(news)
    setIsModalOpen(true)
  }

  const handleModalClose = (open: boolean) => {
    setIsModalOpen(open)
    if (!open) {
      setSelectedNews(null) // Clear selected news when modal closes to prevent stale content
      if (deepLinkNewsId) {
        setLocation("/industry-news", { replace: true }) // drop ?news= so it doesn't reopen
      }
    }
  }

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['/api/industry-news', selectedStatus, sortOrder],
    queryFn: async ({ pageParam }): Promise<NewsListResponse> => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(pageParam) })
      if (selectedStatus !== 'all') params.set('status', selectedStatus)
      if (sortOrder !== 'newest') params.set('sort', sortOrder)
      // apiRequest (not bare fetch): errors keep .status/.data so the global 402/403
      // access gate in queryClient.ts sees them like every other query's
      return apiRequest('GET', `/api/industry-news?${params.toString()}`)
    },
    initialPageParam: 0,
    // Global defaults set staleTime: Infinity, so refetchOnWindowFocus: true would never
    // fire (v5 only refetches STALE queries on focus). 'always' bypasses staleness so the
    // stats + n8n results actually refresh when the user returns to the tab.
    refetchOnWindowFocus: 'always',
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((n, p) => n + p.items.length, 0)
      return loaded < lastPage.total ? loaded : undefined
    },
    // Poll only while a visible article is still waiting for its posts from n8n;
    // stops by itself once every article has posts (plus refetch on window focus).
    refetchInterval: (query) => {
      const pages = (query.state.data as InfiniteData<NewsListResponse> | undefined)?.pages
      const waiting = pages?.some(page => page.items.some(item => item.postsCount === 0))
      return waiting ? 30000 : false
    },
  })

  // stats and total are page-independent server-side — read both from the first page
  const newsItems = data?.pages.flatMap(page => page.items) ?? []
  const stats = data?.pages[0]?.stats
  const total = data?.pages[0]?.total ?? 0

  const toggleStatusFilter = (status: Exclude<StatusFilter, "all">) => {
    setSelectedStatus(prev => (prev === status ? "all" : status))
  }

  if (isLoading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-44 rounded-xl" />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">Fehler beim Laden der Industry News. Bitte versuchen Sie es später erneut.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <AnimatedSection>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3" data-testid="heading-industry-news">
              <Newspaper className="h-8 w-8 text-blue-600" />
              Branchen-News
            </h1>
            <p className="text-muted-foreground mt-2">
              KI-generierte Social Media Posts basierend auf aktuellen Branchen-News
            </p>
          </div>
        </div>
      </AnimatedSection>

      {/* Stats */}
      <AnimatedSection delay={100}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard
            title="Gesamt News"
            value={stats?.total ?? 0}
            hint="News-Artikel"
            icon={Newspaper}
            testId="stat-total-news"
            active={selectedStatus === "all"}
            onClick={() => setSelectedStatus("all")}
          />
          <StatCard
            title="Ausstehend"
            value={stats?.pending ?? 0}
            hint="Noch kein Post veröffentlicht"
            icon={statusConfig.pending.icon}
            testId="stat-pending"
            active={selectedStatus === "pending"}
            onClick={() => toggleStatusFilter("pending")}
          />
          <StatCard
            title="Freigegeben"
            value={stats?.approved ?? 0}
            hint="Mind. ein Post veröffentlicht oder geplant"
            icon={statusConfig.approved.icon}
            testId="stat-approved"
            active={selectedStatus === "approved"}
            onClick={() => toggleStatusFilter("approved")}
          />
          <StatCard
            title="Hohe Relevanz"
            value={stats?.highRelevance ?? 0}
            hint="Score ≥ 70/100"
            icon={TrendingUp}
            testId="stat-high-relevance"
          />
        </div>
      </AnimatedSection>

      {/* Filter */}
      <AnimatedSection delay={200}>
        <div className="flex flex-wrap items-center gap-3">
          <Filter className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <Select value={selectedStatus} onValueChange={(v) => setSelectedStatus(v as StatusFilter)}>
            <SelectTrigger className="w-56" data-testid="select-status" aria-label="Nach Status filtern">
              <SelectValue placeholder="Status wählen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              <SelectItem value="pending">Ausstehend</SelectItem>
              <SelectItem value="approved">Freigegeben</SelectItem>
            </SelectContent>
          </Select>
          <ArrowUpDown className="h-4 w-4 text-muted-foreground ml-2" aria-hidden="true" />
          <Select value={sortOrder} onValueChange={(v) => setSortOrder(v as SortOrder)}>
            <SelectTrigger className="w-48" data-testid="select-sort" aria-label="Sortierung">
              <SelectValue placeholder="Sortierung" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Neueste zuerst</SelectItem>
              <SelectItem value="oldest">Älteste zuerst</SelectItem>
              <SelectItem value="relevance">Höchste Relevanz</SelectItem>
            </SelectContent>
          </Select>
          {selectedStatus !== "all" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedStatus("all")}
              data-testid="button-clear-filters"
            >
              Zurücksetzen
            </Button>
          )}
          <span className="text-sm text-muted-foreground ml-auto" data-testid="text-result-count">
            {newsItems.length} von {total} angezeigt
          </span>
        </div>
      </AnimatedSection>

      {/* News List */}
      <AnimatedSection delay={300}>
        {newsItems.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-muted-foreground">
                {selectedStatus === "all"
                  ? "Noch keine Industry News vorhanden."
                  : "Keine Industry News mit diesem Status."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {newsItems.map((news) => {
              const statusInfo = statusConfig[news.derivedStatus]
              const StatusIcon = statusInfo.icon

              return (
                <FloatingCard key={news.id}>
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      {news.imageUrl && (
                        <img
                          src={news.imageUrl}
                          alt={news.title}
                          className="w-full aspect-video object-cover rounded-md sm:w-24 sm:h-24 sm:order-2"
                          data-testid={`news-image-${news.id}`}
                        />
                      )}
                      <div className="flex-1 space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge className={statusInfo.color} data-testid={`badge-status-${news.id}`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusInfo.label}
                          </Badge>
                          {news.sourceName && (
                            <Badge variant="outline" data-testid={`badge-source-${news.id}`}>
                              {news.sourceName}
                            </Badge>
                          )}
                          {news.nextScheduledFor && news.nextScheduledPostId && (
                            <ScheduledPlanLink
                              postId={news.nextScheduledPostId}
                              scheduledFor={news.nextScheduledFor}
                              testId={`badge-scheduled-${news.id}`}
                            />
                          )}
                        </div>
                        <CardTitle className="text-xl" data-testid={`news-title-${news.id}`}>
                          {news.title}
                        </CardTitle>
                        {news.summary && (
                          <CardDescription className="line-clamp-2" data-testid={`news-summary-${news.id}`}>
                            {news.summary}
                          </CardDescription>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Metrics */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 border-t">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Relevanz-Score</p>
                        <RelevanceScore score={news.relevanceScore} />
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2">
                        {news.sourceUrl && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(news.sourceUrl!, '_blank', 'noopener,noreferrer')}
                            className="w-full sm:w-auto"
                            data-testid={`button-source-${news.id}`}
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Quelle öffnen
                          </Button>
                        )}
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleViewPosts(news)}
                          disabled={news.postsCount === 0}
                          className="w-full sm:w-auto"
                          data-testid={`button-view-posts-${news.id}`}
                        >
                          {news.postsCount === 0 ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Posts werden erstellt…
                            </>
                          ) : (
                            <>
                              <Eye className="w-4 h-4 mr-2" />
                              Posts ansehen ({news.postsCount})
                            </>
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteConfirmNews(news)}
                          className="text-destructive hover:text-destructive"
                          aria-label="News löschen"
                          title="News löschen"
                          data-testid={`button-delete-${news.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </FloatingCard>
              )
            })}

            {hasNextPage && (
              <div className="flex justify-center pt-2">
                <Button
                  variant="outline"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  data-testid="button-load-more"
                >
                  {isFetchingNextPage ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  Mehr laden
                </Button>
              </div>
            )}
          </div>
        )}
      </AnimatedSection>

      {/* Posts Review Modal */}
      {selectedNews && (
        <NewsPostsModal
          open={isModalOpen}
          onOpenChange={handleModalClose}
          newsItem={selectedNews}
          initialPlatform={deepLinkPlatform}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirmNews} onOpenChange={(open) => !open && setDeleteConfirmNews(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>News löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie diese Industry News wirklich löschen?
              Alle zugehörigen Posts werden ebenfalls gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConfirmNews && deleteMutation.mutate(deleteConfirmNews.id)}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Löschen..." : "Löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
